// Render a frozen snapshot of the live multitrack mix to one stereo AudioBuffer
// via OfflineAudioContext, then (optionally) to a WAV Blob for a plain <audio>.
//
// The mix graph mirrors the live player: per input a BufferSource → Gain → Pan
// → destination. The solo/mute logic is NOT duplicated here — the caller
// (player.getMixSnapshot) resolves each track's audible gain and passes it in,
// so `gain: 0` already means "muted or excluded by solo".

import { audioBufferToWavBlob } from './wav-encode';

export interface MixInput {
  /** Decoded stem buffer. */
  buffer: AudioBuffer;
  /** Solo/mute-resolved audible gain (0..1); 0 = silent. */
  gain: number;
  /** Stereo pan, [-1, +1]. */
  pan: number;
}

export interface RenderOpts {
  sampleRate: number;
  durationSec: number;
}

type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

/** Render the mix snapshot to a stereo AudioBuffer (offline, faster than realtime). */
export async function renderMix(
  inputs: MixInput[],
  opts: RenderOpts,
  OfflineCtor: OfflineCtor = OfflineAudioContext,
): Promise<AudioBuffer> {
  const length = Math.max(1, Math.ceil(opts.durationSec * opts.sampleRate));
  const ctx = new OfflineCtor(2, length, opts.sampleRate);
  const canPan =
    typeof (ctx as { createStereoPanner?: () => StereoPannerNode }).createStereoPanner === 'function';

  for (const input of inputs) {
    const src = ctx.createBufferSource();
    src.buffer = input.buffer;
    const gain = ctx.createGain();
    gain.gain.value = input.gain;
    src.connect(gain);
    if (canPan) {
      const panner = ctx.createStereoPanner();
      panner.pan.value = input.pan;
      gain.connect(panner);
      panner.connect(ctx.destination);
    } else {
      gain.connect(ctx.destination);
    }
    src.start(0);
  }

  return ctx.startRendering();
}

/** Render the mix and encode it to a 16-bit PCM WAV Blob for a plain <audio>. */
export async function renderMixToBlob(
  inputs: MixInput[],
  opts: RenderOpts,
  OfflineCtor: OfflineCtor = OfflineAudioContext,
): Promise<Blob> {
  return audioBufferToWavBlob(await renderMix(inputs, opts, OfflineCtor));
}
